import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { search, type SearchResult } from '@/lib/api/search';

const typeIcon: Record<string, string> = {
  job: 'construction',
  client: 'groups',
  invoice: 'receipt_long',
  quote: 'request_quote',
  employee: 'badge',
};

const typeLabel: Record<string, string> = {
  job: 'Job', client: 'Client', invoice: 'Invoice', quote: 'Quote', employee: 'Employee',
};

interface Props {
  onClose: () => void;
}

export const GlobalSearch = ({ onClose }: Props) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      setResults(await search(q));
      setSelected(0);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 200);
  };

  const goTo = (r: SearchResult) => {
    navigate(r.link);
    onClose();
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
    if (e.key === 'Enter' && results[selected]) goTo(results[selected]);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-24 px-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <span className="material-symbols-outlined text-gray-400 text-xl">search</span>
          <input
            ref={inputRef}
            value={query}
            onChange={handleChange}
            onKeyDown={handleKey}
            placeholder="Search jobs, clients, invoices…"
            className="flex-1 text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
          />
          {loading && <div className="w-4 h-4 border-2 border-[#C77D3F] border-t-transparent rounded-full animate-spin" />}
          <kbd className="text-xs text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">Esc</kbd>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="max-h-80 overflow-y-auto py-1">
            {results.map((r, i) => (
              <div
                key={`${r.type}-${r.id}`}
                onClick={() => goTo(r)}
                className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer ${i === selected ? 'bg-[#FAF6F0]' : 'hover:bg-gray-50'}`}
                onMouseEnter={() => setSelected(i)}
              >
                <span className="material-symbols-outlined text-[#C77D3F] text-lg">
                  {typeIcon[r.type] ?? 'search'}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{r.label}</p>
                  {r.status && <p className="text-xs text-gray-400 capitalize">{r.status}</p>}
                </div>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                  {typeLabel[r.type]}
                </span>
              </div>
            ))}
          </div>
        )}

        {query.length >= 2 && !loading && results.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-400">No results for "{query}"</div>
        )}

        {query.length === 0 && (
          <div className="px-4 py-5 text-xs text-gray-400 flex items-center gap-2">
            <kbd className="border border-gray-200 rounded px-1.5 py-0.5">↑↓</kbd> navigate &nbsp;
            <kbd className="border border-gray-200 rounded px-1.5 py-0.5">↵</kbd> open &nbsp;
            <kbd className="border border-gray-200 rounded px-1.5 py-0.5">Esc</kbd> close
          </div>
        )}
      </div>
    </div>
  );
};
